import type {GetDataCloudContext, SalesforceConsent} from './types'

const toConsent = (granted: boolean): SalesforceConsent => ({
  provider: 'OneTrust',
  purpose: 'Tracking',
  status: granted ? 'Opt In' : 'Opt Out'
})

export const hasConsentGroup = (groups: string | string[] | undefined | null, group: string) => {
  if (!groups) return false
  const list = Array.isArray(groups) ? groups : groups.split(',')
  return list.some((g) => g.trim() === group)
}

/**
 * Tracks the consent last given to the SDK so only real changes are forwarded.
 * Opt Out is only sent after an Opt In, as the SDK tracks nothing until it gets one.
 */
export const createConsentTracker = (consentGroup: string, oneTrustEnabled: boolean) => {
  let optedIn = false
  const isGranted = (groups: string | string[] | undefined | null) =>
    !oneTrustEnabled || hasConsentGroup(groups, consentGroup)

  return {
    isOptedIn: () => optedIn,
    initial(groups: string | string[] | undefined | null): SalesforceConsent[] {
      optedIn = isGranted(groups)
      return optedIn ? [toConsent(true)] : []
    },
    next(groups: string | string[] | undefined | null): SalesforceConsent | null {
      const granted = isGranted(groups)
      if (granted === optedIn) return null
      optedIn = granted
      return toConsent(granted)
    }
  }
}

export const subscribeToOneTrustChanges = (onChange: (groups: string[]) => void) => {
  const handler = (event: Event) => onChange((event as CustomEvent).detail)
  window.addEventListener('OneTrustGroupsUpdated', handler)
  return () => window.removeEventListener('OneTrustGroupsUpdated', handler)
}

// Dispatched by the SDK's Data 360 module before each send; detail.events is what it posts
export const DATA_CLOUD_BEFORE_SEND_EVENT = 'dataCloud:onBeforeEventSend'

/**
 * The SDK builds consentLog events itself with only provider, purpose and status,
 * so the customer number and ID are added just before they are sent.
 */
export const addCustomerToConsentLogs = (getContext: GetDataCloudContext) => {
  const handler = (event: Event) => {
    const events: unknown = (event as CustomEvent).detail?.events
    if (!Array.isArray(events)) return
    const consentLogs = events.filter((e) => e?.eventType === 'consentLog')
    if (!consentLogs.length) return

    const {customerNo, customerId} = getContext()
    consentLogs.forEach((consentLog) => {
      if (customerNo) consentLog.customerNo = customerNo
      if (customerId) consentLog.customerId = customerId
    })
  }
  document.addEventListener(DATA_CLOUD_BEFORE_SEND_EVENT, handler)
  return () => document.removeEventListener(DATA_CLOUD_BEFORE_SEND_EVENT, handler)
}
